import { useState, useCallback } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileText, Shield, ArrowRightLeft, Info } from "lucide-react";
import type { Deal } from "../api/types";
import { resolveConflict } from "../api/client";
import { prettyFieldLabel } from "../utils/labels";

interface DataQualityWarning {
  category: string;
  severity: "high" | "medium" | "low";
  message: string;
}

interface ExpensePool {
  pool_id: string;
  pool_name: string;
  pool_type: string;
  total_amount: number;
  _os_original_total?: number;
  _overwritten_by?: string;
  items?: Array<{ line_item_name: string; source?: string; current_year_amount: number }>;
}

interface Props {
  deal: Deal;
  dealId: string;
  documents?: Array<{ filename: string; parse_result?: Record<string, unknown> | null }>;
  onResolved?: () => void;
}

const SEVERITY_STYLES = {
  high: { bg: "bg-red-50", border: "border-red-200", icon: "text-red-500", text: "text-red-800" },
  medium: { bg: "bg-honey-50", border: "border-honey-200", icon: "text-honey-600", text: "text-honey-800" },
  low: { bg: "bg-grove-50", border: "border-grove-200", icon: "text-bark-400", text: "text-bark-600" },
};

export default function DataQualityReport({ deal, dealId, documents, onResolved }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);

  const handleResolve = useCallback(async (body: { conflict_type: string; pool_id?: string; choice: string; tenant_name?: string }) => {
    const key = `${body.conflict_type}-${body.pool_id || body.tenant_name}`;
    setResolving(key);
    try {
      await resolveConflict(dealId, body);
      onResolved?.();
    } catch {
      // silently fail — user can retry
    } finally {
      setResolving(null);
    }
  }, [dealId, onResolved]);
  const pd = (deal.property_data ?? {}) as Record<string, unknown>;
  const warnings = (pd.data_quality_warnings ?? []) as DataQualityWarning[];
  const pools = (pd.expense_pools ?? []) as ExpensePool[];
  const unclassifiedCount = (pd.unclassified_lease_count ?? 0) as number;
  const tenants = (pd.tenants ?? []) as Array<Record<string, unknown>>;

  // Collect parse warnings from documents (truncation, token limits)
  const parseWarnings: Array<{ type: string; severity: string; message: string; filename: string }> = [];
  for (const doc of documents ?? []) {
    const pw = (doc.parse_result?._parse_warnings ?? []) as Array<{ type: string; severity: string; message: string }>;
    for (const w of pw) {
      parseWarnings.push({ ...w, filename: doc.filename });
    }
  }

  // Count provenance overrides
  const overwrittenPools = pools.filter(p => p._overwritten_by);
  const camSourcedItems = pools.flatMap(p => (p.items ?? []).filter(i => i.source === "cam_reconciliation"));
  const osSourcedItems = pools.flatMap(p => (p.items ?? []).filter(i => i.source === "operating_statement"));

  // Count defaults applied
  const defaultsApplied: string[] = [];
  if (unclassifiedCount > 0) {
    defaultsApplied.push(`${unclassifiedCount} tenant${unclassifiedCount > 1 ? "s" : ""} with unclassified lease type (defaulted)`);
  }
  const tenantsWithNoEscalation = tenants.filter(
    t => !t.rent_escalation_type || t.rent_escalation_type === "flat"
  ).length;
  if (tenantsWithNoEscalation > 0 && tenantsWithNoEscalation < tenants.length) {
    defaultsApplied.push(`${tenantsWithNoEscalation} tenant${tenantsWithNoEscalation > 1 ? "s" : ""} with flat/no rent escalation`);
  }

  const highCount = warnings.filter(w => w.severity === "high").length + parseWarnings.filter(w => w.severity === "high").length;
  const mediumCount = warnings.filter(w => w.severity === "medium").length + parseWarnings.filter(w => w.severity === "medium").length;
  const totalIssues = warnings.length + defaultsApplied.length + overwrittenPools.length + parseWarnings.length;

  if (totalIssues === 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-forest-50 border border-forest-200 rounded-xl text-sm text-forest-700">
        <CheckCircle2 className="w-4 h-4" />
        <span className="font-medium">Data quality checks passed</span>
        <span className="text-forest-500">— no cross-document discrepancies detected</span>
      </div>
    );
  }

  return (
    <div className="border border-grove-200 rounded-xl overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-grove-50 hover:bg-grove-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <Shield className={`w-5 h-5 ${highCount > 0 ? "text-red-500" : mediumCount > 0 ? "text-honey-600" : "text-bark-400"}`} />
          <div>
            <p className="text-sm font-display font-semibold text-bark-800">Data Quality Report</p>
            <p className="text-xs text-bark-400 mt-0.5">
              {highCount > 0 && <span className="text-red-600 font-medium">{highCount} critical</span>}
              {highCount > 0 && mediumCount > 0 && <span> &middot; </span>}
              {mediumCount > 0 && <span className="text-honey-700 font-medium">{mediumCount} warning{mediumCount > 1 ? "s" : ""}</span>}
              {(highCount > 0 || mediumCount > 0) && (defaultsApplied.length > 0 || overwrittenPools.length > 0) && <span> &middot; </span>}
              {overwrittenPools.length > 0 && <span>{overwrittenPools.length} override{overwrittenPools.length > 1 ? "s" : ""}</span>}
              {overwrittenPools.length > 0 && defaultsApplied.length > 0 && <span> &middot; </span>}
              {defaultsApplied.length > 0 && <span>{defaultsApplied.length} default{defaultsApplied.length > 1 ? "s" : ""}</span>}
            </p>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-bark-400" /> : <ChevronRight className="w-4 h-4 text-bark-400" />}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 py-4 space-y-4 bg-white">
          {/* Parse warnings (truncation, token limits) */}
          {parseWarnings.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Document Extraction Warnings
              </h4>
              <div className="space-y-2">
                {parseWarnings.map((w, i) => {
                  const s = SEVERITY_STYLES[w.severity as keyof typeof SEVERITY_STYLES] || SEVERITY_STYLES.medium;
                  return (
                    <div key={i} className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${s.bg} ${s.border}`}>
                      <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${s.icon}`} />
                      <div className="text-xs">
                        <span className={`font-medium ${s.text}`}>{w.filename}</span>
                        <p className={`mt-0.5 ${s.text} opacity-90`}>{w.message}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cross-document warnings */}
          {warnings.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Cross-Document Checks
              </h4>
              <div className="space-y-2">
                {warnings.map((w, i) => {
                  const s = SEVERITY_STYLES[w.severity] || SEVERITY_STYLES.low;
                  return (
                    <div key={i} className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${s.bg} ${s.border}`}>
                      <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${s.icon}`} />
                      <div className="text-xs">
                        <span className={`font-medium ${s.text}`}>
                          {prettyFieldLabel(w.category).replace(/\bCross Doc\b/i, "").trim() || "Warning"}
                        </span>
                        <p className={`mt-0.5 ${s.text} opacity-90`}>{w.message}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Provenance — expense pool overrides */}
          {overwrittenPools.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <ArrowRightLeft className="w-3.5 h-3.5" />
                Expense Pool Overrides
              </h4>
              <div className="space-y-1.5">
                {overwrittenPools.map(pool => {
                  const key = `pool_total-${pool.pool_id}`;
                  return (
                    <div key={pool.pool_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-grove-50 border border-grove-200 text-xs">
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-bark-400 shrink-0" />
                        <span className="text-bark-700">
                          <span className="font-medium">{pool.pool_name}</span>: OS
                          <span className="text-bark-400"> ${(pool._os_original_total ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                          <span className="text-bark-400 mx-1">&rarr;</span>
                          CAM rec
                          <span className="font-medium text-forest-700"> ${pool.total_amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                        </span>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          onClick={() => handleResolve({ conflict_type: "pool_total", pool_id: pool.pool_id, choice: "os" })}
                          disabled={resolving === key}
                          className="px-2 py-1 text-[10px] font-medium bg-white border border-grove-300 rounded-md hover:bg-grove-100 disabled:opacity-50"
                        >
                          Use OS
                        </button>
                        <button
                          onClick={() => handleResolve({ conflict_type: "pool_total", pool_id: pool.pool_id, choice: "cam_rec" })}
                          disabled={resolving === key}
                          className="px-2 py-1 text-[10px] font-medium bg-forest-600 text-white rounded-md hover:bg-forest-700 disabled:opacity-50"
                        >
                          Keep CAM
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Data sources summary */}
          {(camSourcedItems.length > 0 || osSourcedItems.length > 0) && (
            <div>
              <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Data Sources
              </h4>
              <div className="flex gap-4 text-xs text-bark-600">
                {osSourcedItems.length > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="w-2 h-2 rounded-full bg-blue-400" />
                    {osSourcedItems.length} items from Operating Statement
                  </span>
                )}
                {camSourcedItems.length > 0 && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1.5 bg-forest-50 border border-forest-200 rounded-lg">
                    <span className="w-2 h-2 rounded-full bg-forest-400" />
                    {camSourcedItems.length} items from CAM Reconciliation
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Defaults applied */}
          {defaultsApplied.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-bark-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                Defaults Applied
              </h4>
              <div className="space-y-1">
                {defaultsApplied.map((d, i) => (
                  <p key={i} className="text-xs text-bark-500 flex items-center gap-2 px-3 py-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-bark-300 shrink-0" />
                    {d}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
